<template>
  <div>
    <!-- Hero stats -->
    <div class="grid grid-cols-12 gap-x-3 h-full relative">
      <div v-if="loadingInsights"
        class="bg-gray-100 opacity-50 rounded-md absolute top-0 left-0 w-full h-full z-50 flex justify-center items-center">
        <BaseLoadingSpinnerCircle class="text-icon-normal w-8 h-8" />
      </div>
      <!-- Top row -->
      <div class="col-span-8 grid grid-cols-6 row-span-2 gap-4">
        <LensInsightCard class="col-span-3" title="Total Spend" tooltip="Total spend in current date range"
          :value="summary.total.spend"
          :percent="summary.diff.spend"
          :loading="loadingInsights"  
        />
        <LensInsightCard class="col-span-3" :title="`Goal Metric Avg. ${getGoalMetricName()}`" tooltip="Edit your goal metric in Lens settings"
          :value="summary.total.goalMetric"
          :percent="summary.diff.goalMetric" 
          :loading="loadingInsights"  
        />
        <LensBenchmarkCard class="col-span-6 rounded-2xl border border-border-normal" 
          :data="lensInsights"
          :loading="loadingInsights" 
          :currency-region="lens?.currency"
        />
      </div>
      <div class="col-span-4 space-y-3 rounded-2xl border border-border-normal p-3 h-full">
        <div class="flex gap-1 items-center">
          <BaseText class="text-text-normal" type="label" size="sm">
            New Creative Velocity
          </BaseText>
          <div class="relative">
            <InfoSimpleIcon class="cursor-pointer text-icon-normal tooltip-trigger" :width="20" :height="20" />
            <transition>
              <div class="tooltip-container text-center px-2 py-1 bg-neutral-800 rounded-md shadow-md cursor-default"
                style="width: 140px;">
                <BaseText type="label" size="xs" class="text-white">
                  Number of creatives tested in current date range
                </BaseText>
              </div>
            </transition>
          </div>
        </div>
        <BaseText class="text-text-muted" type="display" size="2xs">
          {{ getIsPrivateMode ? '•••' : summary.total.creatives }}
        </BaseText>
        <PercentageChangeIndicator :percent="summary.diff.creatives" period-text="since last period" />
        <div v-if="noLaunchTargets" class="rounded-lg p-1 bg-background-disabled flex flex-col justify-between gap-1" style="min-height:140px">
          <div class="p-4 text-center space-y-1">
            <BaseText class="text-text-muted" type="label" size="sm">
              No Targets Set
            </BaseText>
            <BaseText class="text-text-normal" size="sm" >
              Identify winners & losers of<br/>creative tests.
            </BaseText>
          </div>
          <button class="target-btn flex items-center gap-1 justify-center" @click="$emit('changeView', 'creative-tests')">
            <TargetGoalIcon class="text-icon-normal"/>
            <BaseText class="text-text-muted" type="label" size="sm">
              Set Targets
            </BaseText>
          </button>
        </div>
        <!-- Launch analysis -->
        <div class="p-1 space-y-1" v-else>
          <div v-for="launchStat in LAUNCH_STATS" :key="launchStat.key" class="flex items-center w-full py-1">
            <component :is="launchStat.icon" class="text-icon-normal" />
            <BaseText class="text-text-muted flex-1 ml-1" type="label" size="sm">
              {{ launchStat.name }}
            </BaseText>
            <BaseText class="text-text-muted" type="label" size="sm">
              {{ getIsPrivateMode ? '•••' : launchData?.[launchStat.key]?.current }}
            </BaseText>
          </div>
          <button
            class="pl-2 py-1.5 pr-3 flex items-center gap-1 hover:bg-background-normal transition-colors rounded-md w-full"
            @click="$emit('changeView', 'creative-tests')">
            <BaseText class="text-text-muted" type="label" size="sm">
              Analyze in Creative Tests
            </BaseText>
            <ChevronIcon class="text-icon-normal transform -rotate-90" />
          </button>
        </div>
      </div>
    </div>
    <!-- Recent reports -->
    <div class="mt-6 space-y-2">
      <div class="flex items-center justify-between">
        <BaseText type="label" class="text-text-muted">
          Recent Reports
        </BaseText>
        <button
          class="py-1.5 pl-2.5 pr-1.5 flex items-center gap-1 hover:bg-background-normal transition-colors rounded-md"
          @click="$emit('changeView', 'reports')">
          <BaseText class="text-text-muted" type="label" size="sm">
            All reports
          </BaseText>
          <ChevronIcon class="text-icon-normal transform -rotate-90" />
        </button>
      </div>
      <div v-if="loading.recentReports" class="flex items-center w-full h-44 gap-3">
        <div v-for="loadingBox in [1,2,3,4,5]" :key="`skeleton-report-${loadingBox}`" class="skeleton w-44 h-44 rounded-2xl"/>
      </div>
      <div v-else-if="recentReports.length > 0" class="flex items-center gap-3 fade-in">
        <!-- Report box -->
        <button v-for="report in recentReports" :key="report.id"
          class="rounded-2xl overflow-hidden border border-border-normal w-44 h-44 group hover:bg-background-normal hover:border-border-hover transition-colors"
          @mouseenter="shownDescription = report.id" @mouseleave="shownDescription = null"
          @click="navToReport(report.id)">
          <!-- <BaseText type="label" size="sm" class="text-text-muted p-4">{{ report.name }}</BaseText> -->
          <img class="w-28 h-28 mx-auto -mt-4" :src="reportTypes[report.type]?.img">
          <div class="px-4 space-y-1">
            <div class="flex items-center gap-1 w-full">
              <component :is="reportTypes[report.type]?.icon" class="flex-shrink-0 text-icon-normal" />
              <BaseText type="label" size="sm"
                class="capitalize text-text-muted whitespace-nowrap flex-1 truncate group-hover:text-text-muted text-left">
                {{ report.name }}
              </BaseText>
            </div>
            <BaseHeightTransition>
              <BaseText v-if="report.description && shownDescription === report.id" size="sm"
                class="text-text-normal report-desc text-left">
                {{ report.description }}
              </BaseText>
            </BaseHeightTransition>
          </div>
        </button>
      </div>
      <div v-else class="flex items-center justify-center w-full py-8">
        <BaseText class="text-text-normal" type="label" size="sm">
          No reports
        </BaseText>
      </div>
    </div>
    <div class="mt-6 bg-background-normal rounded-xl relative overflow-hidden">
      <div v-if="loadingInsights"
        class="bg-gray-100 opacity-50 rounded-md absolute top-0 left-0 w-full h-full z-50 flex justify-center items-center">
        <BaseLoadingSpinnerCircle class="text-icon-normal w-8 h-8" />
      </div>
      <div class="w-full mt-2 p-2 flex items-center justify-center gap-0.5">
        <LeaderboardRank class="flex-shrink-0 text-icon-normal" display-only />
        <BaseText type="label" class="text-text-muted">
          Top Creative Leaderboard
        </BaseText>
      </div>
      <div class="top-ads-grid-wrapper">
        <div class="top-ads-grid relative">
          <img class="absolute bottom-0 -right-44 opacity-60" src="../../assets/images/laurel.svg">
          <div v-for="(ad, index) in topAds.ads" :key="ad.group_by" class="relative min-w-0">
            <img v-if="index === topAds.ads.length - 1" class="absolute bottom-0 -right-44 opacity-60"
            src="../../assets/images/laurel.svg">
            <GridGraphCard
              :is-ad-level="true"
              :data="ad"
              :selected-kpis="topAds.kpis"
              :data-summary="{ currency: lens?.currency }"
              @showDetailsDrawer="$emit('showDetailsDrawer', ad)"
            />
            <img v-if="index === 0" class="absolute bottom-0 -left-44 opacity-60" style="transform: scale(-1,1)"
            src="../../assets/images/laurel.svg">
          </div>
        </div>
      </div>
    </div>
    <!-- Table -->
    <div
      class="mt-2 border border-border-normal rounded-xl grid grid-cols-9 overflow-hidden table-header-container relative">
      <div v-if="loadingInsights"
        class="bg-gray-100 opacity-50 rounded-md absolute top-0 left-0 w-full h-full z-50 flex justify-center items-center">
        <BaseLoadingSpinnerCircle class="text-icon-normal w-8 h-8" />
      </div>
      <!-- Header -->
      <div v-for="(header, index) in topAds.tableHeaders" :key="header" class="p-3 w-full border-b border-border-normal"
        :class="[index === 0 ? 'col-span-3' : 'col-span-2', index === topAds.tableHeaders.length - 1 ? '' : 'border-r']">
        <BaseText class="whitespace-nowrap w-full text-text-subdued uppercase"
          :class="index === 0 ? 'text-left' : 'text-center'" type="heading" size="overline">
          {{ header }}
        </BaseText>
      </div>
      <!-- Row data -->
      <div v-for="(ad, adIndex) in topAds.allAds" :key="ad.group_by" @click="$emit('showDetailsDrawer', ad)"
      class="col-span-9 grid grid-cols-9 ad-table-container cursor-pointer">
        <div class="flex items-center gap-3 col-span-3 w-full">
          <div class="flex items-center gap-2 overflow-hidden p-3 w-full">
            <LeaderboardRank v-show="adIndex + 1 <= 5" class="flex-shrink-0" :rank="adIndex + 1" />
            <div class="w-12 h-12 rounded-md flex-shrink-0 overflow-hidden">
              <img
                :style="{ filter: getIsPrivateMode ? 'blur(8px)' : 'none' }"
                :src="ad.ad_image_url"
                class="w-full h-full"
              >
            </div>
            <BaseText type="label" size="sm" class="text-text-muted truncate whitespace-nowrap">
              {{ getIsPrivateMode ? '•••••••••••' : ad.ad_name }}
            </BaseText>
          </div>
        </div>
        <div class="p-3 col-span-2 ">
          <BaseText class="text-text-muted text-center" size="sm">
            {{ getDayDifference(ad.min_event_date, ad.max_event_date) }} Days
          </BaseText>
        </div>
        <div class="p-3 col-span-2 ">
          <BaseText class="text-text-muted text-center" size="sm">
            {{ getIsPrivateMode ? '••••••' : formatNumber(ad.spend, 'currency') }}
          </BaseText>
        </div>
        <div class="p-3 col-span-2 border-b border-border-normal">
          <BaseText class="text-text-muted text-center" size="sm">
            {{ getIsPrivateMode ? '•••' : formatNumber(ad?.[lensInsights.goal_metric], getMetricLookup?.[lensInsights.goal_metric].type) }}
          </BaseText>
        </div>
      </div>
      <!-- Net result -->
      <div class="bg-background-normal grid grid-cols-9 col-span-9 net-result-container">
        <div class="col-span-3 px-2.5 py-3 flex items-center gap-3.5">
          <InfoSimpleIcon class="text-icon-disabled" />
          <BaseText type="label" size="sm" class="text-text-muted">
            Net Result
          </BaseText>
        </div>
        <div class="col-span-2 text-text-muted" size="sm">
          Avg. {{ getIsPrivateMode ? '••••' : netAdResults.totalDays / topAds.allAds.length }}
        </div>
        <div class="col-span-2 text-text-muted" size="sm">
          {{ getIsPrivateMode ? '••••' : formatNumber(netAdResults.totalSpend, 'currency') }}
        </div>
        <div class="col-span-2 text-text-muted" size="sm">
          Avg. {{ getIsPrivateMode ? '••••' : formatNumber(netAdResults?.[lensInsights.goal_metric] / netAdResults.totalSpend,
            getMetricLookup?.[lensInsights.goal_metric].type) }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import LensAPI from '@/api/lens'
import { mapGetters } from 'vuex'
import { formatNumberLabel } from '../../utils/lens/formatNumberLabel'
import formatLabelString from '../../utils/lens/formatGraphLabelString'
// Components
import PercentageChangeIndicator from '../../components/lens/PercentageChangeIndicator.vue'
import GridGraphCard from '../../components/lens/graphs/grid/GridGraphCard.vue'
import LensInsightCard from '../../components/lens/LensInsightCard.vue'
import LensBenchmarkCard from '../../components/lens/LensBenchmarkCard.vue'
import LeaderboardRank from '../../components/lens/LeaderboardRank.vue'
// Icons
import TargetGoalIcon from '../../components/globals/Icons/TargetGoalIcon.vue'
import BenchmarkIcon from '../../components/globals/Icons/LensIcons/BenchmarkIcon.vue'
import InfoSimpleIcon from '../../components/globals/Icons/InfoSimpleIcon.vue'
import NewLaunchedIcon from '../../components/globals/Icons/NewLaunchedIcon.vue'
import WinnerIcon from '../../components/globals/Icons/WinnerIcon.vue'
import SignDirectionIcon from '../../components/globals/Icons/SignDirectionIcon.vue'
import ChevronIcon from '../../components/globals/Icons/ChevronIcon.vue'
import ScaleIcon from '../../components/globals/Icons/ScaleIcon.vue'
import TopPerformingIcon from '../../components/lens/report/TopPerformingIcon.vue'
import ComparaitiveIcon from '../../components/lens/report/ComparaitiveIcon.vue'
import AdDetailsAnimatedIcon from '../../components/globals/Icons/AdDetailsAnimatedIcon.vue'

export default {
  name: 'LensMainView',
  components: {
    PercentageChangeIndicator,
    LensInsightCard,
    LensBenchmarkCard,
    GridGraphCard,
    LeaderboardRank,
    // Icons
    InfoSimpleIcon,
    BenchmarkIcon,
    ChevronIcon,
    NewLaunchedIcon,
    ScaleIcon,
    WinnerIcon,
    TargetGoalIcon,
    SignDirectionIcon,
    AdDetailsAnimatedIcon
  },
  props: {
    lens: {
      type: Object,
      default: () => { }
    },
    lensInsights: {
      type: Object,
      default: () => { }
    },
    loadingInsights: {
      type: Boolean,
      default: false
    }
  },
  data() {
    const LAUNCH_STATS = [
      { name: 'New Launched', key: 'launched', value: 0, icon: NewLaunchedIcon },
      { name: 'Scaling', key: 'scaling', value: 0, icon: ScaleIcon },
      { name: 'Winners', key: 'winners', value: 0, icon: WinnerIcon },
      { name: 'Losers', key: 'losers', value: 0, icon: SignDirectionIcon }
    ]

    const reportTypes = {
      'top-performing': {
        img: require('../../assets/images/lens-report-top.png'),
        icon: TopPerformingIcon
      },
      comparative: {
        img: require('../../assets/images/lens-report-comparative.png'),
        icon: ComparaitiveIcon
      }
    }

    return {
      LAUNCH_STATS,
      reportTypes,

      loading: {
        recentReports: false
      },

      shownDescription: null,

      recentReports: [],

      hoveredCreativeDetails: null
    }
  },
  watch: {
    getPendingLocalReportUpdate (newUpdate) {
      // If a report was updated in the sidebar, we need to propogate those changes here
      if (newUpdate?.id && newUpdate?.data) {
        // Update the report in state if it has the same id as the update
        const updateReports = this.recentReports.map(report => (
          report.id === newUpdate.id ? { ...report, ...newUpdate.data } : report
        ))
        this.recentReports = updateReports
      } else if (newUpdate?.id && newUpdate?.isDeleted) {
        // Remove the deleted report from local state
        this.recentReports = this.recentReports.filter(report => report.id !== newUpdate.id)
      }
    }
  },
  computed: {
    ...mapGetters('MetricsModule', ['getMetricLookup']),
    ...mapGetters('LensModule', ['getPendingLocalReportUpdate']),
    ...mapGetters('MiscModule', ['getIsPrivateMode']),
    launchData () {
      return this.lensInsights.targets?.insights || {}
    },
    noLaunchTargets () {
      const targetInsight = this.lensInsights?.targets
      return targetInsight === null
    },
    netAdResults() {
      const goalMetric = this.lensInsights.goal_metric
      return this.lensInsights.insights.ads.reduce((acc, curr) => {
        acc.totalDays += this.getDayDifference(curr.min_event_date, curr.max_event_date)
        acc.totalSpend += curr.spend
        acc[goalMetric] += curr.spend * curr[goalMetric]
        return acc
      }, { totalDays: 0, totalSpend: 0, [goalMetric]: 0 })
    },
    topAds() {
      const ads = this.lensInsights.insights.ads || []
      const goalKey = this.lensInsights.goal_metric
      const goalName = this.getMetricLookup[goalKey].name
      return {
        tableHeaders: ['Rank & Creative', 'Days running', 'Spend', `Goal Metric (${goalName})`],
        kpis: ['spend', goalKey],
        ads: ads.slice(0, 3),
        allAds: ads
      }
    },
    summary() {
      const data = this.lensInsights.insights
      const prevData = this.lensInsights.insights?.previous_period || {}
      const goalKey = this.lensInsights.goal_metric
      return {
        total: {
          spend: formatLabelString(data.spend, 'currency', this.lens?.currency),
          goalMetric: this.formatNumber(data?.[goalKey], this.getMetricLookup[goalKey].type),
          creatives: data.creatives_count
        },
        diff: {
          spend: this.computePercentDifference(data.spend, prevData.spend),
          goalMetric: this.computePercentDifference(data?.[goalKey], prevData?.[goalKey]),
          creatives: this.computePercentDifference(data?.creatives_count, prevData.creatives_count)
        }
      }
    }
  },
  async mounted() {
    if (!this.lensInsights?.insights){
      this.$showAlert({type: 'error', message: 'No data available for this lens'})
    }
    this.loading.recentReports = true
    try {
      const res = await LensAPI.LensReport.getAll({ lens_id: this.lensInsights.id })
      this.recentReports = res.data.splice(0, 6)

    } catch (e) {
      this.$showAlert({ type: 'error', message: 'Error fetching recent reports' })
    } finally {
      this.loading.recentReports = false
    }
  },
  methods: {
    formatNumber(value, type) {
      return formatNumberLabel(value, type, this.lensInsights.currency)
    },
    getDayDifference(start, end) {
      if (!start || !end) return 0
      const startDate = new Date(start)
      const endDate = new Date(end)
      const differenceInTime = endDate - startDate
      return Math.ceil(differenceInTime / (1000 * 60 * 60 * 24))
    },
    computePercentDifference(val, prevVal) {
      return (((val) - prevVal) / prevVal) * 100
    },
    navToReport(reportId) {
      this.$router.push({ name: 'LensReport', params: { lensId: this.lensInsights.id, reportId } })
    },
    getGoalMetricName () {
      const goalMetricKey = this.lensInsights.goal_metric
      return this.getMetricLookup?.[goalMetricKey]?.name || ''
    }
  }
}
</script>
<style scoped>
.tooltip-container {
  pointer-events: none;
  position: absolute;
  bottom: calc(100% + 4px);
  left: 50%;
  transform: translateX(-50%) translateY(0);
  opacity: 0;
  z-index: 40005;
}

.tooltip-trigger:hover+.tooltip-container {
  opacity: 1
}

.report-type-img {
  position: absolute;
  left: 0;
  right: 0;
  margin-left: auto;
  margin-right: auto;
  top: -60px;
  width: 255px;
  height: 255px;
  transition: filter 150ms ease-in-out;
}

.report-type-btn:hover>>>.report-type-img {
  filter: brightness(90%);
}

.report-desc {
  display: -webkit-box;
  line-clamp: 2;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.img-type-active {
  filter: brightness(90%);
}

.top-ads-grid-wrapper {
  width: 100%;
  margin: 20px 0;
  max-height: 32rem;
}

.top-ads-grid {
  padding-left: 56px;
  padding-right: 56px;
  display: grid;
  gap: 8px;
  justify-content: center;

  grid-auto-flow: column;
  grid-auto-columns: minmax(0, 320px); /* starts at 320, can shrink */
  width: 100%;
}

.ad-table-container {
  transition: background-color 100ms ease-in-out;
}
.ad-table-container:hover {
  background-color: rgba(248, 250, 251, 0.5);
}

.ad-table-container>div {
  height: 72px;
}

.ad-table-container>div:not(:first-child),
.net-result-container>div:not(:first-child) {
  display: flex;
  align-items: center;
  justify-content: center;
}

.net-result-container>div:not(:last-child) {
  border-right: 1px solid #ECEFF3;
}

.ad-table-container>div:not(:last-child) {
  border-right: 1px solid #ECEFF3;
  border-bottom: 1px solid #ECEFF3
}

/* Section transitions */
.expand-enter-active,
.expand-leave-active {
  transition-property: height;
  transition-duration: 100ms;
}

.expand-enter,
.expand-leave-to {
  opacity: 0;
}

.fade-in {
  animation: fadeIn 150ms ease-in-out;
}
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* .target-btn{
  background: linear-gradient(0deg, #FFFFFF, #FFFFFF),
linear-gradient(0deg, color(display-p3 1.000 1.000 1.000), color(display-p3 1.000 1.000 1.000)),
linear-gradient(180deg, rgba(223, 225, 231, 0) 0%, rgba(223, 225, 231, 0.08) 100%),
linear-gradient(180deg, color(display-p3 0.875 0.882 0.906 / 0) 0%, color(display-p3 0.875 0.882 0.906 / 0.08) 100%);
padding: 8px 6px;
box-shadow: 0px 0px 0px 1px #12376914;
box-shadow: 0px 1px 2px 0px #09194821;
} */

.target-btn {
  padding: 6px 8px;
  border-radius: 6px;
  background: white;
  box-shadow: 0px 1px 2px 0px rgba(4, 26, 75, 0.13), 0px 0px 0px 1px rgba(0, 56, 108, 0.08);
  transition: box-shadow 100ms ease-in-out, background-color 100ms ease-in-out;
}

.target-btn:hover {
  box-shadow: 0px 1px 2px 0px rgba(4, 26, 75, 0.17), 0px 0px 0px 1px rgba(0, 56, 108, 0.12);
}
</style>
